import React, { useState, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import Cropper from "react-easy-crop";
import { Slider } from "antd";
import { ReactComponent as CloseIcon } from "../../../Assets/Icons/CloseIconSvg.svg";
import { updateMerchantBusinessProfile } from "../../../services/merchantService";
import { uploadFile } from "../../../utils/s3";
import { getCroppedImgFile } from "../../../utils/cropImage";
import "../../common/OfferForm/offerform.scss";

const ChangeLogoModal = ({ merchantDetails, onClose, onSuccess }) => {
    const dispatch = useDispatch();
    const fileInputRef = useRef(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    const [isRemovingLogo, setIsRemovingLogo] = useState(false);

    // Cropping states
    const [isCropping, setIsCropping] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [sourceImage, setSourceImage] = useState(null);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
            if (!validTypes.includes(file.type)) {
                setError("Please select a valid image file (JPEG, PNG, or WebP)");
                return;
            }

            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                setError("File size must be less than 5MB");
                return;
            }

            setError("");
            setIsRemovingLogo(false);

            // Create source image URL for cropper
            const reader = new FileReader();
            reader.onloadend = () => {
                setSourceImage(reader.result);
                setIsCropping(true);
                setZoom(1);
                setCrop({ x: 0, y: 0 });
            };
            reader.readAsDataURL(file);
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveAction = () => {
        if (selectedFile || isCropping) {
            setSelectedFile(null);
            setPreviewUrl(null);
            setSourceImage(null);
            setIsCropping(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        } else if (merchantDetails?.logo) {
            setIsRemovingLogo(true);
            setPreviewUrl(null);
        }
    };

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleCropSave = async () => {
        try {
            const croppedFile = await getCroppedImgFile(
                sourceImage,
                croppedAreaPixels,
                0,
                { horizontal: false, vertical: false },
                "merchant_logo.jpg"
            );
            setSelectedFile(croppedFile);
            setPreviewUrl(URL.createObjectURL(croppedFile));
            setIsCropping(false);
        } catch (e) {
            console.error(e);
            setError("Failed to crop image");
        }
    };

    const handleCropCancel = () => {
        setIsCropping(false);
        setSourceImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleClose = () => {
        // Check if there are unsaved changes
        if ((selectedFile || isRemovingLogo || isCropping) && !uploading) {
            setShowDiscardConfirm(true);
        } else {
            onClose();
        }
    };

    const handleDiscardConfirm = () => {
        setShowDiscardConfirm(false);
        onClose();
    };

    const handleDiscardCancel = () => {
        setShowDiscardConfirm(false);
    };

    const handleSubmit = async () => {
        if (!selectedFile && !isRemovingLogo) {
            setError("Please select a logo to upload or remove the current one");
            return;
        }

        setUploading(true);
        setError("");

        try {
            let logoValue = "";

            if (selectedFile) {
                // Upload to S3 if a new file is selected
                logoValue = await uploadFile(selectedFile, selectedFile.name);
                if (!logoValue) {
                    throw new Error("Failed to upload image to S3");
                }
            } else if (isRemovingLogo) {
                // If removing, logoValue remains empty string
                logoValue = "";
            }

            const response = await dispatch(
                updateMerchantBusinessProfile({
                    id: merchantDetails._id,
                    data: { logo: logoValue },
                    customMessage: "Logo Updated Successfully"
                })
            ).unwrap();

            if (response?.success) {
                onSuccess && onSuccess();
                onClose();
            } else {
                setError(response?.message || "Failed to update logo");
            }
        } catch (err) {
            console.error("Error updating logo:", err);
            setError(err?.message || "Failed to update logo. Please try again.");
        } finally {
            setUploading(false);
        }
    };

    const hasChanges = !!selectedFile || isRemovingLogo;

    return (
        <div
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-black bg-opacity-50"
            style={{ animation: "fadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)" }}
        >
            <style>
                {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          @keyframes slideIn {
            from { transform: translateY(-20px) scale(0.95); opacity: 0; }
            to { transform: translateY(0) scale(1); opacity: 1; }
          }
        `}
            </style>

            <div className="offerform w-full flex justify-center">
                <div
                    className="bg-white rounded-[12px] pt-8 px-8 pb-6 w-[90%] max-w-[540px] shadow-xl relative"
                    style={{ animation: "slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)" }}
                >
                    {/* Close button inside modal at top-right corner */}
                    <button
                        onClick={handleClose}
                        className="absolute -top-5 -right-5 w-[40px] h-[40px] flex items-center justify-center z-10 hover:scale-105 transition-transform"
                    >
                        <CloseIcon className="w-full h-full" />
                    </button>

                    {/* Header */}
                    <div className="flex justify-between items-start mb-4">
                        <h2 className="text-[22px] font-[600] text-[#303030] font-poppins">
                            Change Merchant Logo
                        </h2>
                    </div>

                    {/* Merchant Info */}
                    <div className="mb-4">
                        <p className="text-[18px] font-[600] text-[#303030] mb-1">
                            Merchant Name
                        </p>
                        <p className="text-[16px] font-[500] text-[#767676]">
                            {merchantDetails?.businessName || "N/A"}
                        </p>
                    </div>

                    {/* Logo Section */}
                    <div className="mb-4">
                        <p className="text-[18px] font-[600] text-[#303030] mb-2">
                            Current Logo
                        </p>
                        <div className="flex flex-col items-center">
                            {isCropping ? (
                                <div className="w-full flex flex-col items-center">
                                    {/* Cropper Container */}
                                    <div className="relative w-full h-[240px] bg-[#767676] rounded-[8px] overflow-hidden mb-8">
                                        <Cropper
                                            image={sourceImage}
                                            crop={crop}
                                            zoom={zoom}
                                            aspect={1}
                                            cropShape="round"
                                            showGrid={false}
                                            onCropChange={setCrop}
                                            onCropComplete={onCropComplete}
                                            onZoomChange={setZoom}
                                        />
                                    </div>

                                    {/* Zoom Slider */}
                                    <div className="w-full flex items-center gap-4 mb-10">
                                        <span className="text-[16px] font-[600] text-[#303030] whitespace-nowrap">Zoom</span>
                                        <div className="flex-1">
                                            <Slider
                                                min={1}
                                                max={3}
                                                step={0.1}
                                                value={zoom}
                                                onChange={(value) => setZoom(value)}
                                                tooltip={{ open: false }}
                                                className="offer-crop-slider"
                                            />
                                        </div>
                                    </div>

                                    {/* Cropper Action Buttons */}
                                    <div className="flex gap-4 w-full justify-end mb-4">
                                        <button
                                            onClick={handleCropCancel}
                                            className="px-10 py-2 border border-[#00ADEF] rounded-[8px] text-[16px] font-[600] text-[#00ADEF] hover:bg-[#F0FAFF] transition-colors min-w-[140px]"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleCropSave}
                                            className="px-10 py-2 bg-[#00ADEF] rounded-[8px] text-[16px] font-[600] text-white transition-colors min-w-[140px]"
                                        >
                                            Upload
                                        </button>
                                    </div>
                                </div>
                            ) : previewUrl || (merchantDetails?.logo && !isRemovingLogo) ? (
                                <>
                                    <div className="w-[140px] h-[140px] rounded-full border-2 border-[#00ADEF33] flex items-center justify-center overflow-hidden bg-white mb-10">
                                        <img
                                            src={
                                                previewUrl || (
                                                    merchantDetails.logo.startsWith("http") || merchantDetails.logo.startsWith("data:")
                                                        ? merchantDetails.logo
                                                        : `${process.env.REACT_APP_S3_URL || ""}${merchantDetails.logo}`
                                                )
                                            }
                                            alt="Merchant Logo"
                                            className="w-full h-full object-contain"
                                        />
                                    </div>

                                    {/* Action Buttons below logo */}
                                    <div className="flex gap-3 w-full justify-end mb-4">
                                        <button
                                            onClick={handleRemoveAction}
                                            disabled={uploading || (!selectedFile && !merchantDetails?.logo) || isRemovingLogo}
                                            className="px-8 py-1.5 border border-[#00ADEF] rounded-[6px] text-[15px] font-[600] text-[#00ADEF] hover:bg-[#F0FAFF] transition-colors disabled:opacity-30 disabled:cursor-not-allowed min-w-[110px]"
                                        >
                                            Remove
                                        </button>
                                        <button
                                            onClick={handleSubmit}
                                            disabled={uploading}
                                            className="px-8 py-1.5 bg-[#00ADEF] rounded-[6px] text-[15px] font-[600] text-white transition-colors disabled:opacity-50 min-w-[110px] flex items-center justify-center gap-2"
                                        >
                                            {uploading ? (
                                                <>
                                                    <div className="w-[14px] h-[14px] border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                    Updating...
                                                </>
                                            ) : (
                                                "Update Logo"
                                            )}
                                        </button>
                                    </div>
                                </>
                            ) : (
                                <div
                                    onClick={handleUploadClick}
                                    className="flex flex-col items-center cursor-pointer my-6 py-4"
                                >
                                    <div className="w-[140px] h-[140px] rounded-full border border-[#00ADEF] flex items-center justify-center bg-[#F0FAFF] mb-4 hover:bg-[#E6F7FF] transition-colors">
                                        <span className="text-[#00ADEF] text-[18px] font-[600]">
                                            No Logo
                                        </span>
                                    </div>
                                    <p className="text-[#303030] text-[16px] font-[500]">
                                        Click to <span className="text-[#00ADEF] font-[600]">upload</span>
                                    </p>
                                </div>
                            )}
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp"
                            onChange={handleFileSelect}
                            className="hidden"
                        />
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mt-4 p-3 bg-[#FFF1F0] border border-[#FFCCC7] rounded-[6px]">
                            <p className="text-[13px] font-[400] text-[#FF4D4F] text-center">{error}</p>
                        </div>
                    )}
                </div>

                {/* Discard Changes Confirmation Dialog */}
                {showDiscardConfirm && (
                    <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-50">
                        <div className="bg-white rounded-[12px] p-6 w-[90%] max-w-[400px] shadow-xl">
                            <h3 className="text-[18px] font-[600] text-[#303030] mb-3">
                                Discard changes?
                            </h3>
                            <p className="text-[14px] font-[400] text-[#767676] mb-6">
                                You have unsaved changes. Are you sure you want to discard them?
                            </p>
                            <div className="flex gap-3 justify-end">
                                <button
                                    onClick={handleDiscardCancel}
                                    className="px-6 py-2 border border-[#D9D9D9] rounded-[6px] text-[14px] font-[500] text-[#303030] hover:bg-[#F5F5F5] transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDiscardConfirm}
                                    className="px-6 py-2 bg-[#FF4D4F] rounded-[6px] text-[14px] font-[500] text-white hover:bg-[#D9363E] transition-colors"
                                >
                                    Discard
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div >
    );
};

export default ChangeLogoModal;
